/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useMemo, useState } from 'react';
import { Route, Routes } from 'react-router';
import { AppRoutes } from '@/constants/routes';
import { useBreadCrumbs } from '@/hooks/useBreadCrumbs';
import { AnchorDiv, CBreadCrumbs, CNavLink } from '@/components';
import { JobsMonitorPage } from './JobsPage/JobsMonitorPage';
import { JobDetailPage } from './JobsPage/JobDetailsPage';
import { JobProcessHistoryPage } from './JobsPage/JobProcessHistoryPage';
import { jobCommander, useQueryFilters } from '@/hooks/useQueryFilters';
import { Utils } from '@/utils/utils';

export const JobsPage: React.FC = () => {
  const { homeLink, jobListLink } = useBreadCrumbs();

  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [processId, setProcessId] = useState<string | undefined>(undefined);

  const { prefilters } = useQueryFilters({ commander: jobCommander, filter_prefix: 'job' });

  const detailTemplateLink = useMemo(() => {
    if (jobId) {
      return {
        template: () => (
          <CNavLink className="text-md hover:underline" to={AppRoutes.jobs.detail.url(jobId)}>
            {jobId}
          </CNavLink>
        )
      };
    }
  }, [jobId]);

  const jobHistoryLink = useMemo(() => {
    if (processId) {
      const reference = (prefilters.reference as string) || undefined;
      const _processId = Utils.hideProcessPrefix(processId);
      return {
        template: () => (
          <CNavLink
            className="text-md hover:underline"
            to={AppRoutes.jobs.processHistory.url(processId) + Utils.buildWithQueryParamsUrl({})}
          >
            {reference && <span className="">{reference} / </span>}
            {_processId?.split('/').join(' / ')}
          </CNavLink>
        )
      };
    }
  }, [processId, prefilters.reference]);

  const breadCrumbModelLinks = useMemo(() => {
    const model = [jobListLink];

    if (jobHistoryLink) {
      model.push(jobHistoryLink);
    }

    if (detailTemplateLink) {
      model.push(detailTemplateLink);
    }
    return model;
  }, [jobListLink, jobHistoryLink, detailTemplateLink]);

  return (
    <AnchorDiv className="w-full py-1 h-full">
      <CBreadCrumbs className="my-2" home={homeLink} model={breadCrumbModelLinks} />
      <Routes>
        <Route index element={<JobsMonitorPage />} />
        <Route
          path={AppRoutes.jobs.detail.path}
          element={<JobDetailPage setJobId={setJobId} setProcessId={setProcessId} />}
        />
        <Route
          path={AppRoutes.jobs.processHistory.path}
          element={<JobProcessHistoryPage setProcessId={setProcessId} />}
        />
      </Routes>
    </AnchorDiv>
  );
};
